import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { buildReplyWithFilterableMenuItems } from '../utils/attachButtonMenuItemBuilder';
import { aFPCreateAttachmentHandler } from '../utils/createAttachmentHandler';
import focusToEditor from '../utils/focusToEditor';
import { filterSuggestions } from '../utils/getAttachmentCommandItems';
import getSensitivitySubMenu, { getLearnMoreSubMenuItem } from '../utils/getSensitivitySubMenu';
import { insertLinkHandler } from '../utils/insertLinkHandler';
import logAttachButtonClickedDatapoint from '../utils/logAttachButtonClickedDatapoint';
import logAttachMenuData from '../utils/logAttachMenuData';
import logAttachMenuDataOnClose from '../utils/logAttachMenuDataOnClose';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { ICommandBarStyleProps, ICommandBarStyles } from 'office-ui-fabric-react/lib/CommandBar';
import { IContextualMenuProps } from 'office-ui-fabric-react/lib/ContextualMenu';
import { Spinner } from 'office-ui-fabric-react/lib/Spinner';
import { IStyleFunctionOrObject } from 'office-ui-fabric-react/lib/Utilities';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { getMRUAttachmentStore } from 'owa-attachment-suggestions';
import { CreateAttachmentsProperties } from 'owa-attachment-well-data';
import { ControlIcons } from 'owa-control-icons';
import isEncryptionEnabled from 'owa-encryption-common/lib/utils/isEncryptionEnabled';
import isEncryptionTemplateAvailable from '../utils/isEncryptionTemplateAvailable';
import { isFeatureEnabled } from 'owa-feature-flags';
import { DisplayOption, FilterableCommandBar, FilterableMenuItem } from 'owa-filterable-menu';
import loc from 'owa-localize';
import { accCheckerMenuItemText } from 'owa-locstrings/lib/strings/acccheckermenuitemtext.locstring.json';
import { attachTooltip } from 'owa-locstrings/lib/strings/attachtooltip.locstring.json';
import { composeDiscardButton } from 'owa-locstrings/lib/strings/composediscardbutton.locstring.json';
import { composeSendButton } from 'owa-locstrings/lib/strings/composesendbutton.locstring.json';
import { highImportanceMenuText } from 'owa-locstrings/lib/strings/highimportancemenutext.locstring.json';
import { insertSignature as insertSignature_1 } from 'owa-locstrings/lib/strings/insertsignature.locstring.json';
import { lowImportanceMenuText } from 'owa-locstrings/lib/strings/lowimportancemenutext.locstring.json';
import { normalImportanceMenuText } from 'owa-locstrings/lib/strings/normalimportancemenutext.locstring.json';
import { popoutsOpenInNewWindow } from 'owa-locstrings/lib/strings/popoutsopeninnewwindow.locstring.json';
import { saveDraft } from 'owa-locstrings/lib/strings/savedraft.locstring.json';
import { sending } from 'owa-locstrings/lib/strings/sending.locstring.json';
import { sensitivityMenuText } from 'owa-locstrings/lib/strings/sensitivitymenutext.locstring.json';
import { setImportance as setImportance_1 } from 'owa-locstrings/lib/strings/setimportance.locstring.json';
import { showFrom } from 'owa-locstrings/lib/strings/showfrom.locstring.json';
import { showMessageOptions } from 'owa-locstrings/lib/strings/showmessageoptions.locstring.json';
import { switchToPlainTextMenuItem } from 'owa-locstrings/lib/strings/switchtoplaintextmenuitem.locstring.json';
import { getStore as getCLPStore } from 'owa-mail-clp/lib/store/store';
import isCLPEnabled from 'owa-mail-clp/lib/utils/isCLPEnabled';
import clickAttachButton from 'owa-mail-compose-actions/lib/actions/clickAttachButton';
import discardCompose from 'owa-mail-compose-actions/lib/actions/discardCompose';
import { openComposeWithViewState } from 'owa-mail-compose-actions/lib/actions/openCompose';
import protectMessage from 'owa-mail-compose-actions/lib/actions/protectMessage';
import setImportance from 'owa-mail-compose-actions/lib/actions/setImportance';
import setIsDirty from 'owa-mail-compose-actions/lib/actions/setIsDirty';
import switchBodytype from 'owa-mail-compose-actions/lib/actions/switchBodyType';
import toggleFrom from 'owa-mail-compose-actions/lib/actions/toggleFrom';
import trySaveMessage from 'owa-mail-compose-actions/lib/actions/trySaveMessage';
import { findComposePlugins } from 'owa-mail-compose-actions/lib/plugins/ComposePlugins';
import isPublicFolderComposeViewState from 'owa-mail-compose-store/lib/utils/isPublicFolderComposeViewState';
import handleOnSend from 'owa-mail-compose-actions/lib/utils/handleOnSend';
import loadAttachmentSuggestions from 'owa-mail-compose-actions/lib/utils/loadAttachmentSuggestions';
import shouldDisableEncryptionButton from 'owa-mail-compose-actions/lib/utils/shouldDisableEncryptionButton';
import { ComposeTarget, ComposeViewState, getStore, UIEnabledState } from 'owa-mail-compose-store';
import { showMessageOptionsDialog } from 'owa-mail-message-options-view';
import isDeepLink from 'owa-popout/lib/utils/isDeepLink';
import ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { isConsumer } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { SurfaceActionIcons } from 'owa-surface-action-icons';
import isMessageOptionsEnabled from 'owa-surface-actions/lib/utils/isMessageOptionsEnabled';
import * as React from 'react';
import {
    composeEllipsisAriaLabelText,
    composePostButton,
    encryptMenuText,
    encryptDisableForwardingMenuText,
    hideFrom,
    switchToHTMLMenuItem,
} from '../strings.locstring.json';
import {
    composeProtectButton,
    composeSetPermissionDropDown,
} from 'owa-locstrings/lib/strings/composeprotectbutton.locstring.json';
import {
    getEncryptionDropDownSubMenu,
    getIRMTemplatesDropDown,
} from '../utils/getEncryptionDropDownSubMenu';
import {
    getDefaultButtonsList,
    getSmimeAttachButtonsList,
} from '../utils/getAttachmentCommandItems';
import {
    AttachmentMultiMenuEntry,
    attachmentMultiMenuEntry,
    GetCreateAttachmentHandlerFunc,
} from 'owa-attachment-multi-menu-actions';
import {
    AttachmentSuggestions,
    ClientItem,
    mailStore,
    ReplyWithAttachmentSuggestion,
} from 'owa-mail-store';
import {
    AttachmentLocalComputerFilePicker,
    IAttachmentLocalComputerFilePicker,
    FilePickerAction,
    FileSelectionFilter,
} from 'owa-attachment-filepicker';
import {
    ENCRYPT_GUID,
    ENCRYPT_DNF_GUID,
    IRM_DEFAULT_GUID,
} from 'owa-mail-compose-actions/lib/store/schema/EncryptionGUID';

const styles = require('./ComposeCommandingBar.scss');
const classNames = require('classnames');

type AttachmentMultiMenuMap<T> = { [K in keyof AttachmentMultiMenuEntry]: T };

export interface ComposeCommandingBarProps {
    composeViewState: ComposeViewState;
    styles?: IStyleFunctionOrObject<ICommandBarStyleProps, ICommandBarStyles>;
    className?: string;
}

@observer
export default class ComposeCommandingBar extends React.Component<ComposeCommandingBarProps, {}> {
    private attachmentLocalComputerFilePicker: React.RefObject<
        IAttachmentLocalComputerFilePicker
    > = React.createRef();
    private attachmentLocalComputerToCloudFilePicker: React.RefObject<
        IAttachmentLocalComputerFilePicker
    > = React.createRef();

    render() {
        return (
            <span>
                {this.createAttachmentLocalComputerFilePicker(
                    attachmentMultiMenuEntry.computer.getCreateAttachmentHandler,
                    this.attachmentLocalComputerFilePicker
                )}

                {this.createAttachmentLocalComputerFilePicker(
                    attachmentMultiMenuEntry.uploadAndShareFromCloud.getCreateAttachmentHandler,
                    this.attachmentLocalComputerToCloudFilePicker
                )}

                <FilterableCommandBar
                    styles={this.props.styles}
                    className={classNames(styles.filterableCommandBar, this.props.className)}
                    filterableItems={this.commandingBarItems}
                    elipisisAriaLabel={loc(composeEllipsisAriaLabelText)}
                />
            </span>
        );
    }

    private createAttachmentLocalComputerFilePicker(
        getCreateAttachmentHandler: GetCreateAttachmentHandlerFunc,
        ref: React.Ref<IAttachmentLocalComputerFilePicker>
    ) {
        return (
            <AttachmentLocalComputerFilePicker
                ref={ref}
                fileSelectionFilter={FileSelectionFilter.None}
                action={FilePickerAction.Attach}
                createAttachmentHandler={getCreateAttachmentHandler(
                    this.props.composeViewState.attachmentWell,
                    (files, properties, dataProvider) => {
                        const composePlugins = findComposePlugins(this.props.composeViewState);
                        aFPCreateAttachmentHandler(
                            files,
                            composePlugins.inlineImage,
                            this.props.composeViewState,
                            properties,
                            dataProvider
                        );
                    }
                )}
            />
        );
    }

    @computed
    private get commandingBarItems(): FilterableMenuItem[] {
        const { composeViewState } = this.props;
        if (this.props.composeViewState.isSending) {
            return [this.sendingMenuItem];
        } else {
            let barItems;

            if (isPublicFolderComposeViewState(composeViewState)) {
                barItems = [
                    this.sendMenuItem,
                    this.attachMenuItem,
                    this.protectMenuItem,
                    this.discardMenuItem,
                    this.showFromMenuItem,
                    this.setImportanceMenuItem,
                    this.switchBodyType,
                    this.openAccCheckerMenuItem,
                    this.openInPopoutMenuItem,
                ];
            } else {
                barItems = [
                    this.sendMenuItem,
                    this.attachMenuItem,
                    this.sensitivityMenuItem,
                    this.protectMenuItem,
                    this.discardMenuItem,
                    this.saveDraftMenuItem,
                    this.insertSignatureMenuItem,
                    this.showFromMenuItem,
                    this.protectMenuItemDropDown,
                    this.setPermissionMenuItemDropDown,
                    this.setImportanceMenuItem,
                    this.showMessageOptionsMenuItem,
                    this.switchBodyType,
                    this.openAccCheckerMenuItem,
                    this.openInPopoutMenuItem,
                ];
            }

            return barItems;
        }
    }

    @computed
    private get sendingMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            loc(sending),
            null,
            null,
            () => true /*shouldShow*/,
            DisplayOption.Left,
            false /*disabled*/,
            null,
            null,
            () => {
                return (
                    <div className={styles.sendSpinner}>
                        <Spinner />
                        <span className={styles.sendSpinnerText}>{loc(sending)}</span>
                    </div>
                );
            }
        );
    }

    @computed
    private get sendMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;
        const disabled = composeViewState.uiEnabledState !== UIEnabledState.Enabled;

        let sendLabel: string;
        if (isPublicFolderComposeViewState(composeViewState)) {
            sendLabel = loc(composePostButton);
        } else {
            sendLabel = loc(composeSendButton);
        }

        return new FilterableMenuItem(
            sendLabel,
            ControlIcons.Send,
            () => {
                handleOnSend(composeViewState);
            },
            () => true /*shouldShow*/,
            DisplayOption.Left,
            disabled /* disabled */
        );
    }

    @computed
    private get attachMenuItem(): FilterableMenuItem {
        const composeViewState: ComposeViewState = this.props.composeViewState;
        const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);
        const attachmentMultiMenuEntriesClickHandler: AttachmentMultiMenuMap<() => void> = {
            computer: () => {
                const datapoint = new PerformanceDatapoint('AttachLocalComputerClicked');
                datapoint.addCustomData([AttachmentMenuEntrySource.CommandingBar]);

                if (this.attachmentLocalComputerFilePicker.current) {
                    this.attachmentLocalComputerFilePicker.current.click();
                    datapoint.end();
                } else {
                    datapoint.endWithError(
                        DatapointStatus.ClientError,
                        new Error('Lazy component not loaded')
                    );
                }
            },
            onlineLocation: this.openFilePicker,
            uploadAndShareFromCloud: () => {
                const datapoint = new PerformanceDatapoint('AttachUploadAndSharedClicked');
                datapoint.addCustomData([AttachmentMenuEntrySource.CommandingBar]);

                if (this.attachmentLocalComputerToCloudFilePicker.current) {
                    this.attachmentLocalComputerToCloudFilePicker.current.click();
                    datapoint.end();
                } else {
                    datapoint.endWithError(
                        DatapointStatus.ClientError,
                        new Error('Lazy component not loaded')
                    );
                }
            },
        };

        const attachmentMenus = Object.keys(attachmentMultiMenuEntry).reduce(
            (agg, key: keyof AttachmentMultiMenuEntry) => {
                const attachmentMenuEntry = attachmentMultiMenuEntry[key];

                const aria = attachmentMenuEntry.ariaLabel && attachmentMenuEntry.ariaLabel();
                const menu = new FilterableMenuItem(attachmentMenuEntry.text(), null, () => {
                    attachmentMenuEntry.logUsage(AttachmentMenuEntrySource.CommandingBar);
                    attachmentMultiMenuEntriesClickHandler[key]();
                });
                menu.iconProps = attachmentMenuEntry.iconProps;
                menu.ariaLabel = aria;
                menu.title = aria;

                agg[key] = menu;

                return agg;
            },
            {} as AttachmentMultiMenuMap<FilterableMenuItem>
        );

        const menuItems = [];
        const userConfiguration = getUserConfiguration();

        const classicAttachmentsEnabled =
            userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled;
        const referenceAttachmentsEnabled =
            userConfiguration.AttachmentPolicy.ReferenceAttachmentsEnabled;
        const isPlainText = composeViewState.bodyType !== 'HTML';

        if (classicAttachmentsEnabled) {
            menuItems.push(attachmentMenus.computer);
        }

        if (!isSmimeCompose) {
            if (classicAttachmentsEnabled || referenceAttachmentsEnabled) {
                menuItems.push(attachmentMenus.onlineLocation);
            }
            if (attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled()) {
                menuItems.push(attachmentMenus.uploadAndShareFromCloud);
            }

            // Attachment suggestions (replyWith + MRU)
            const referenceItem: ClientItem = mailStore.items.get(
                composeViewState.referenceItemId && composeViewState.referenceItemId.Id
            );
            const replyWithSuggestions: AttachmentSuggestions = referenceItem && {
                ...referenceItem.AttachmentSuggestions,
            };
            const contextualAttachmentSuggestions =
                referenceItem && referenceItem.ContextualAttachmentSuggestions;
            if (replyWithSuggestions && replyWithSuggestions.sentence) {
                replyWithSuggestions.sentence = replyWithSuggestions.sentence.filter(suggestion =>
                    filterSuggestions(suggestion, !isPlainText)
                );
            }
            const mruSuggestions: ReplyWithAttachmentSuggestion[] = getMRUAttachmentStore()
                .attachments;

            let attachmentSuggestions: FilterableMenuItem[] = [];
            if (!!replyWithSuggestions || !!mruSuggestions || !!contextualAttachmentSuggestions) {
                attachmentSuggestions = buildReplyWithFilterableMenuItems(
                    composeViewState,
                    replyWithSuggestions,
                    [...(contextualAttachmentSuggestions || []), ...(mruSuggestions || [])].filter(
                        suggestion => filterSuggestions(suggestion, !isPlainText)
                    )
                );
            }
            menuItems.push(...attachmentSuggestions);
        }

        const menu: IContextualMenuProps = {
            items: menuItems,
            onMenuOpened: () => {
                if (!isSmimeCompose) {
                    loadAttachmentSuggestions(composeViewState);
                }

                logAttachButtonClickedDatapoint(
                    composeViewState.referenceItemId && composeViewState.referenceItemId.Id
                );

                // Log info about the buttons shown in the menu.
                logAttachMenuData(
                    composeViewState.referenceItemId,
                    isSmimeCompose
                        ? getSmimeAttachButtonsList()
                        : getDefaultButtonsList(!isPlainText)
                );
            },
            onMenuDismissed: () => {
                // Log that the menu has been dismissed.
                logAttachMenuDataOnClose(composeViewState.referenceItemId);
            },
        };

        // If both classic and cloudy attachments are disabled then we disable the button
        const attachmentsDisabled =
            (!classicAttachmentsEnabled && !referenceAttachmentsEnabled) || this.shouldDisable();

        return new FilterableMenuItem(
            loc(attachTooltip),
            ControlIcons.Attach,
            null, // onClick
            () => true, // shouldShow
            null, // displayOption
            attachmentsDisabled, // disabled
            menu,
            null, // key
            null, // onRender
            null, // checked
            null, // ariaLabel
            null, // title
            null, // className
            null, // canCheck
            null // iconOnly
        );
    }

    private openFilePicker = () => {
        const { composeViewState } = this.props;
        const composePlugins = findComposePlugins(composeViewState);
        const inlineImagePlugin = composePlugins.inlineImage;
        const lazyLinkPlugin = composePlugins.link;

        // The inlineImagePlugin may be null if the editor has already been disposed
        if (inlineImagePlugin) {
            clickAttachButton(
                false,
                params => {
                    const baseProperties = { isInline: params.isInline, isHiddenInline: false };
                    const properties: CreateAttachmentsProperties = params.shouldShare
                        ? { ...baseProperties, shouldShare: true }
                        : { ...baseProperties, shouldShare: false };

                    aFPCreateAttachmentHandler(
                        params.files,
                        inlineImagePlugin,
                        composeViewState,
                        properties,
                        params.dataProviderType
                    );
                },
                params => {
                    insertLinkHandler(params.files, lazyLinkPlugin);
                },
                composeViewState
            );
        }
    };

    @computed
    private get sensitivityMenuItem(): FilterableMenuItem {
        const composeViewState: ComposeViewState = this.props.composeViewState;
        const isSmimeCompose = isSmimeEnabledInViewState(composeViewState.smimeViewState);
        let { userLabels, learnMoreUrl } = getCLPStore();

        let menu = getSensitivitySubMenu(userLabels, composeViewState, isSmimeCompose);
        if (learnMoreUrl) {
            menu.push(...getLearnMoreSubMenuItem(learnMoreUrl));
        }

        return new FilterableMenuItem(
            loc(sensitivityMenuText), // name
            ControlIcons.MarkAsProtected, // icon
            null, // onclick
            () => isCLPEnabled(), // shouldShow
            null, // displayOptions
            this.shouldDisable() /* disabled */,
            menu // menuContent
        );
    }

    @computed
    private get protectMenuItemDropDown(): FilterableMenuItem {
        // There are two variables controls the dropdown: CLPEnabled, RmsV2UIEnabled
        // 1. CLPEnabled=true and RmsV2UIEabled=true/false, hide "Encrypt" button and drop down has two buttons (encrypt only and DNF)
        // 2. CLPEnabled=false and RmsV2UIEnabled=true, hide drop down and show "Encrypt" button.
        // 3. CLPEnabled=false and RmsV2UIEnabled=false, hide "Encrypt" button and dropdown has all RMS templates.
        // Note: if the user does not have any RMS template and message classification available, then the
        return new FilterableMenuItem(
            loc(composeProtectButton),
            null,
            () => {},
            () => {
                return (
                    isEncryptionEnabled() &&
                    isFeatureEnabled('cmp-clp') &&
                    isEncryptionTemplateAvailable([ENCRYPT_GUID, ENCRYPT_DNF_GUID])
                );
            },
            DisplayOption.Overflow,
            shouldDisableEncryptionButton(this.props.composeViewState) || this.shouldDisable(),
            getEncryptionDropDownSubMenu(this.props.composeViewState, [
                ENCRYPT_GUID,
                ENCRYPT_DNF_GUID,
            ])
        );
    }

    @computed
    private get setPermissionMenuItemDropDown(): FilterableMenuItem {
        const { RmsV2UIEnabled } = getUserConfiguration();
        const storeState = getStore();
        return new FilterableMenuItem(
            loc(composeSetPermissionDropDown),
            null,
            () => {},
            () => {
                return isEncryptionEnabled() && !RmsV2UIEnabled && isEncryptionTemplateAvailable();
            },
            DisplayOption.Overflow,
            shouldDisableEncryptionButton(this.props.composeViewState) &&
                storeState.messageClassifications.size == 0, // This button should be disabled only when both IRMTemplate and MessageClassification are empty.
            getIRMTemplatesDropDown(this.props.composeViewState)
        );
    }

    @computed
    private get protectMenuItem(): FilterableMenuItem {
        const storeState = getStore();
        const { composeViewState } = this.props;
        const shouldDisableEncryption = shouldDisableEncryptionButton(composeViewState);
        const { RmsV2UIEnabled } = getUserConfiguration();

        if (isConsumer()) {
            const encryptMenu = new FilterableMenuItem(
                loc(encryptMenuText),
                null /*icon*/,
                () => {
                    protectMessage(
                        composeViewState,
                        storeState.complianceConfigurations,
                        ENCRYPT_GUID
                    );
                },
                () => true,
                null /*displayOption*/,
                shouldDisableEncryptionButton(composeViewState, ENCRYPT_GUID)
            );

            const dnfMenu = new FilterableMenuItem(
                loc(encryptDisableForwardingMenuText),
                null /*icon*/,
                () => {
                    protectMessage(
                        composeViewState,
                        storeState.complianceConfigurations,
                        ENCRYPT_DNF_GUID
                    );
                },
                () => true,
                null /*displayOption*/,
                shouldDisableEncryptionButton(composeViewState, ENCRYPT_DNF_GUID)
            );

            return new FilterableMenuItem(
                loc(composeProtectButton),
                SurfaceActionIcons.Encryption,
                null /*onClick()*/,
                () => {
                    return (
                        isEncryptionEnabled() &&
                        !shouldDisableEncryption &&
                        isEncryptionTemplateAvailable()
                    );
                },
                null /*displayOption*/,
                shouldDisableEncryption,
                [encryptMenu, dnfMenu]
            );
        } else {
            return new FilterableMenuItem(
                loc(composeProtectButton),
                isFeatureEnabled('cmp-clp') ? null : SurfaceActionIcons.Encryption,
                () => {
                    protectMessage(
                        composeViewState,
                        storeState.complianceConfigurations,
                        IRM_DEFAULT_GUID
                    );
                },
                () => {
                    // When CLP is enabled the "Encrypt" button should be hidden
                    // When RmsV2UIEnabled is false, "Encrypt" button should be hidden
                    return (
                        isEncryptionEnabled() &&
                        !isFeatureEnabled('cmp-clp') &&
                        !shouldDisableEncryption &&
                        RmsV2UIEnabled &&
                        isEncryptionTemplateAvailable()
                    );
                },
                isFeatureEnabled('cmp-clp') ? DisplayOption.Overflow : null,
                shouldDisableEncryption
            );
        }
    }

    @computed
    private get discardMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(loc(composeDiscardButton), ControlIcons.Delete, () => {
            discardCompose(this.props.composeViewState);
        });
    }

    @computed
    private get saveDraftMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            loc(saveDraft),
            null, // no icon
            () => {
                trySaveMessage(this.props.composeViewState);
                focusToEditor(this.props.composeViewState);
            },
            () => {
                return true;
            },
            DisplayOption.Overflow
        );
    }

    @computed
    private get openInPopoutMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            null, // no string
            ControlIcons.OpenInNewWindow,
            () => {
                openComposeWithViewState(this.props.composeViewState, ComposeTarget.Popout);
            },
            () => {
                return !isDeepLink();
            },
            DisplayOption.Right,
            this.shouldDisable() /* disabled */,
            null /* menuContent */,
            null /* key */,
            null /* onRender */,
            null /* checked */,
            loc(popoutsOpenInNewWindow),
            loc(popoutsOpenInNewWindow)
        );
    }

    @computed
    private get insertSignatureMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            loc(insertSignature_1),
            null, // no icon
            () => {
                const datapoint = new PerformanceDatapoint('ComposeCommandInsertSignature');
                const composePlugins = findComposePlugins(this.props.composeViewState);
                composePlugins.contentOperation.insertSignature(
                    this.props.composeViewState.bodyType
                );
                setIsDirty(this.props.composeViewState);
                datapoint.end();
            },
            () => {
                return true;
            },
            DisplayOption.Overflow,
            this.shouldDisable() /* disabled */
        );
    }

    @computed
    private get showFromMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            !this.props.composeViewState.fromViewState.isFromShown ? loc(showFrom) : loc(hideFrom),
            null, // no icon
            () => {
                toggleFrom(this.props.composeViewState);
                focusToEditor(this.props.composeViewState);
            },
            () => {
                return !this.props.composeViewState.fromViewState.isFromReadOnly;
            },
            DisplayOption.Overflow,
            isSmimeEnabledInViewState(this.props.composeViewState.smimeViewState) ||
                this.shouldDisable() /* disabled */
        );
    }

    @computed
    private get setImportanceMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            loc(setImportance_1),
            null, // no icon
            null,
            () => {
                return true;
            },
            DisplayOption.Overflow,
            this.shouldDisable() /* disabled */,
            [
                this.getSetImportanceMenuSubItem(loc(highImportanceMenuText), 'High'),
                this.getSetImportanceMenuSubItem(loc(normalImportanceMenuText), 'Normal'),
                this.getSetImportanceMenuSubItem(loc(lowImportanceMenuText), 'Low'),
            ]
        );
    }

    private getSetImportanceMenuSubItem(
        menuText: string,
        importance: ImportanceType
    ): FilterableMenuItem {
        const { composeViewState } = this.props;

        return new FilterableMenuItem(
            menuText,
            null, // no icon
            () => {
                setImportance(composeViewState, importance);
                focusToEditor(composeViewState);
            },
            null, // shouldShow,
            null, // displayOption,
            null, // disabled,
            null, // menuContent
            null, // key,
            null, // onRender
            composeViewState.importance == importance, // checked
            null, // ariaLabel
            null, // title
            null, // className
            true // canCheck
        );
    }

    @computed
    private get showMessageOptionsMenuItem(): FilterableMenuItem {
        return new FilterableMenuItem(
            loc(showMessageOptions),
            null, // no icon
            () => {
                showMessageOptionsDialog(this.props.composeViewState);
            },
            () => {
                return isMessageOptionsEnabled();
            },
            DisplayOption.Overflow,
            this.shouldDisable() /* disabled */
        );
    }

    @computed
    private get switchBodyType(): FilterableMenuItem {
        const { composeViewState } = this.props;
        const isPlainText = composeViewState.bodyType != 'HTML';
        return new FilterableMenuItem(
            isPlainText ? loc(switchToHTMLMenuItem) : loc(switchToPlainTextMenuItem),
            null, // no icon
            () => switchBodytype(composeViewState),
            () => true, // shouldShow
            DisplayOption.Overflow,
            this.shouldDisable() /* disabled */
        );
    }

    @computed
    private get openAccCheckerMenuItem(): FilterableMenuItem {
        const { composeViewState } = this.props;
        const isPlainText = composeViewState.bodyType != 'HTML';
        return new FilterableMenuItem(
            loc(accCheckerMenuItemText),
            null, // no icon
            this.openAccChecker,
            () => true, // shouldShow
            DisplayOption.Overflow,
            isPlainText || this.shouldDisable() /* disabled */,
            null, // menuContent
            null, // key
            null, // onRender
            composeViewState.accChecker.isOpen, // isChecked
            null, // ariaLabel
            null, // title
            null, // className
            true // canCheck
        );
    }

    private openAccChecker = () => {
        const { composeViewState } = this.props;
        const composePlugins = findComposePlugins(composeViewState);
        const accCheckerPlugin = composePlugins.accChecker;

        // The accCheckerPlugin may be null if the editor has already been disposed
        if (accCheckerPlugin) {
            if (accCheckerPlugin.isImported() && composeViewState.accChecker.isOpen) {
                accCheckerPlugin.getPlugin().then(plugin => plugin.closeChecker());
            } else {
                accCheckerPlugin.addOnImportedCallback(plugin => plugin.openChecker());
                accCheckerPlugin.getPlugin();
            }
        }
    };

    private shouldDisable = () => {
        return this.props.composeViewState.uiEnabledState === UIEnabledState.AllDisabled;
    };
}
